import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, ImageIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { buildAssetUrl } from '../../../../../../utils/imageUrl'

const ImageSlider = ({
  images = [],
  alt = '',
  className = 'h-40',
  rounded = 'rounded-xl',
}) => {
  const { t } = useTranslation()
  const slides = useMemo(
    () =>
      images
        .map((img) => buildAssetUrl(typeof img === 'string' ? img : img?.url))
        .filter(Boolean),
    [images],
  )

  const [index, setIndex] = useState(0)

  useEffect(() => {
    setIndex(0)
  }, [slides])

  const go = (dir) => {
    setIndex((i) => (i + dir + slides.length) % slides.length)
  }

  if (!slides.length) {
    return (
      <div
        className={`grid place-items-center bg-brand-50 text-brand-300 ${rounded} ${className}`}
        aria-label={t('cartesAcces.shared.slider.noImage')}
      >
        <ImageIcon className="h-8 w-8" aria-hidden />
      </div>
    )
  }

  const hasMultiple = slides.length > 1
  const imageAlt = alt
    ? t('cartesAcces.shared.slider.altWithIndex', { alt, n: index + 1 })
    : t('cartesAcces.shared.slider.altIndex', { n: index + 1 })

  return (
    <div className={`group relative mx-auto w-full overflow-hidden ${rounded} ${className}`}>
      <img
        src={slides[index]}
        alt={imageAlt}
        className="h-full w-full object-cover"
      />

      {hasMultiple && (
        <>
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label={t('cartesAcces.shared.slider.prev')}
            className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white opacity-0 transition-opacity hover:bg-black/60 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/70 group-hover:opacity-100"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label={t('cartesAcces.shared.slider.next')}
            className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white opacity-0 transition-opacity hover:bg-black/60 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/70 group-hover:opacity-100"
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={t('cartesAcces.shared.slider.goTo', { n: i + 1 })}
                onClick={() => setIndex(i)}
                className={[
                  'h-1.5 rounded-full transition-all',
                  i === index ? 'w-4 bg-white' : 'w-1.5 bg-white/50 hover:bg-white/80',
                ].join(' ')}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default ImageSlider
